import classNames from 'classnames/bind';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useContext, useState } from 'react';
import Download from '../../../svg/download-simple.svg';
import { LanguageContext } from '../../../utils/LanguageContext';
import { useMounted } from '../../../utils/useMounted';
import Input from '../../common/Input/Input';
import Notification from '../../Notification/Notification';
import * as styles from './Form.module.scss';

const API_ENDPOINT =
	'https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8';
const SUCCESS_MESSAGE = {
	fr: `Votre message a bien été envoyé ! Nous reviendrons vers vous dans les plus brefs délais.`,
	en: `Your message has been sent! We'll get back to you as soon as possible.`,
};
const ERROR_MESSAGE = {
	fr: `Une erreur est survenue. Veuillez réessayer dans un instant.`,
	en: `An error has occurred. Please retry in a while.`,
};

const c = classNames.bind(styles);

function Form({ className, file = {} }) {
	const language = useContext(LanguageContext);
	const query = useStaticQuery(graphql`
		query {
			wp {
				sp {
					strings {
						form_fields_require_label {
							en
							fr
						}
						form_contact_send_label {
							en
							fr
						}
						form_fonction_placeholder {
							en
							fr
						}
						form_fonction_label {
							en
							fr
						}
						form_company_placeholder {
							en
							fr
						}
						form_company_label {
							en
							fr
						}
						form_message_placeholder {
							en
							fr
						}
						form_message_label {
							en
							fr
						}
						form_email_placeholder {
							en
							fr
						}
						form_email_label {
							en
							fr
						}
						form_firstname_placeholder {
							en
							fr
						}
						form_firstname_label {
							en
							fr
						}
						form_lastname_placeholder {
							en
							fr
						}
						form_lastname_label {
							en
							fr
						}
					}
				}
			}
		}
	`);
	const strings = query.wp.sp.strings;
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [company, setCompany] = useState('');
	const [job, setJob] = useState('');
	const [message, setMessage] = useState('');
	const [loading, setLoading] = useState(false);
	const [notif, setNotif] = useState(null);
	const [showDownload, setShowDownload] = useState(false);

	const { isMounted } = useMounted();

	const lang = language.languageCode === 'FR' ? 'fr' : 'en';

	async function handleSubmit(e) {
		e.preventDefault();
		setLoading(true);

		const formData = new FormData();
		if (process.env.GATSBY_ACTIVE_ENV !== 'production') {
			formData.append('debug', 1);
			formData.append('debugEmail', 'blabla@sweetpunk.net');
		}
		formData.append('oid', '00D0Y000002FJfi');
		formData.append('retURL', 'https://levana.io');
		formData.append('first_name', firstName);
		formData.append('last_name', lastName);
		formData.append('email', email);
		formData.append('company', company);
		formData.append('title', job);
		formData.append('lead_source', 'Website');
		formData.append('description', message);
		try {
			await fetch(API_ENDPOINT, {
				method: 'POST',
				mode: 'no-cors',
				body: new URLSearchParams([...formData]),
			});

			setNotif({
				message: SUCCESS_MESSAGE[lang],
				type: 'success',
			});

			if (file.infos) {
				setShowDownload(true);
			}

			if (isMounted.current) {
				setFirstName('');
				setLastName('');
				setEmail('');
				setCompany('');
				setJob('');
				setMessage('');
			}
		} catch (e) {
			setNotif({
				message: ERROR_MESSAGE[lang],
				type: 'error',
			});
		} finally {
			if (isMounted.current) setLoading(false);
		}
	}

	function getLabelAndPlaceholder(fieldName) {
		return {
			label: strings[`form_${fieldName}_label`][lang],
			placeholder: strings[`form_${fieldName}_placeholder`][lang],
		};
	}

	return (
		<section id="form" className={c('wrapper', className)}>
			{file.infos && <p className={c('moreInfos')}>{file.description}</p>}
			{notif && (
				<Notification
					message={notif}
					ended={() => {
						setNotif(null);
					}}
				/>
			)}
			<form className={c('form')} onSubmit={handleSubmit}>
				<Input
					required
					name="first_name"
					value={firstName}
					loading={loading}
					setValue={setFirstName}
					className={c('input', 'firstname')}
					{...getLabelAndPlaceholder('firstname')}
				/>

				<Input
					required
					name="last_name"
					value={lastName}
					loading={loading}
					setValue={setLastName}
					className={c('input', 'lastname')}
					{...getLabelAndPlaceholder('lastname')}
				/>

				<Input
					required
					name="email"
					type="email"
					value={email}
					loading={loading}
					setValue={setEmail}
					className={c('input', 'email')}
					{...getLabelAndPlaceholder('email')}
				/>

				<Input
					name="text"
					value={company}
					loading={loading}
					setValue={setCompany}
					className={c('input', 'company')}
					{...getLabelAndPlaceholder('company')}
				/>

				<Input
					name="title"
					type="text"
					value={job}
					loading={loading}
					setValue={setJob}
					className={c('input', 'job')}
					{...getLabelAndPlaceholder('fonction')}
				/>

				<Input
					required
					textarea
					name="message"
					value={message}
					loading={loading}
					setValue={setMessage}
					className={c('input', 'message')}
					{...getLabelAndPlaceholder('message')}
				/>

				<p className={c('legend')}>
					*&#8239;
					{strings.form_fields_require_label[lang]}
				</p>

				<button type="submit" className={c('submit')}>
					{strings.form_contact_send_label[lang]}
				</button>
			</form>
			{file.infos && (
				<a
					href={file.infos.url}
					rel="noreferrer"
					download
					target="_blank"
					className={c('download', { show: showDownload })}
				>
					{language.languageCode === 'FR'
						? 'Télécharger le PDF'
						: 'Download PDF'}
					<Download />
				</a>
			)}
		</section>
	);
}

export default Form;
